/**
 * The columns of groups down the left of a grid.
 *
 * One column per group by, each holding the runs of rows that share a value at
 * that level. Where the runs *are* is worked out in `grouping.ts`, which is
 * pure; this is what they look like.
 *
 * **One layer, not a cell per row.** A run is one element covering the rows it
 * spans, so a group of forty rows is one box rather than forty repeating the
 * same value -- which is both what it should look like and the only version
 * that is cheap enough to sit beside a grid.
 *
 * It is sticky horizontally and not vertically: the runs line up with rows, so
 * they scroll with them, and they stay put while the columns scroll underneath.
 * A zero-height sticky wrapper is what buys that -- it takes part in the flow
 * at the top of the row spacer, so its absolutely positioned children are
 * placed from the same origin the rows are, while `left` sticks them in place.
 */
import type { Field, ViewGroupBy } from '@/api/types'
import type { FieldContext } from '../fields/behavior/behavior'
import { ValueDisplay } from '../fields/ui/read'
import { groupWidthOf, groupsWidth, type GroupSpan } from './grouping'

export function GroupColumns({
  groupBys,
  fields,
  levels,
  rowHeight,
  context,
}: {
  groupBys: readonly ViewGroupBy[]
  fields: readonly Field[]
  /** One array of runs per group by, in the same order. */
  levels: readonly (readonly GroupSpan[])[]
  rowHeight: number
  context: FieldContext | undefined
}) {
  if (groupBys.length === 0) return null

  let left = 0

  return (
    <div
      // Decorative, and deliberately so. The value a row is grouped under is in
      // the row: it is one of its cells, and every one of those is already
      // announced. Reading it again for every run would say the same thing
      // twice for every row in the group.
      aria-hidden
      data-testid="groups"
      className="sticky left-0 z-10 h-0"
      style={{ width: groupsWidth(groupBys) }}
    >
      {groupBys.map((groupBy, level) => {
        const field = fields.find((one) => one.id === groupBy.field)
        const width = groupWidthOf(groupBy)
        const column = (
          <div
            key={groupBy.id}
            className="absolute top-0 border-r bg-background"
            style={{ left, width }}
          >
            {(levels[level] ?? []).map((span) => (
              <div
                key={span.start}
                className="absolute flex items-center gap-2 overflow-hidden border-b px-2 text-sm"
                style={{
                  top: span.start * rowHeight,
                  height: span.rows * rowHeight,
                  width,
                }}
              >
                {/* A group by on a field this reader cannot see still orders the
                    rows, and `grouping.ts` gives every such row a run of its
                    own. Drawing the run empty is the honest version of that:
                    the rows are grouped, by something not on offer here. */}
                {field !== undefined && (
                  <ValueDisplay field={field} value={span.value} context={context} />
                )}
                {/* A group the server has not counted shows nothing rather
                    than a zero, which would be a group with rows visibly
                    sitting in it. Unguarded, because `null` renders as nothing
                    on its own and a condition saying so is a condition no test
                    can tell from its absence. */}
                <span className="ml-auto shrink-0 text-xs text-muted-foreground">
                  {span.count}
                </span>
              </div>
            ))}
          </div>
        )
        left += width
        return column
      })}
    </div>
  )
}
